import { BLOG_POST_DEFAULTS } from "./blogPosts";
import { getLocalizedValue, type SupportedLocale } from "./localization";
import { legacySitePath } from "./publicHome";
import { SOLUTION_DEFAULTS, SOLUTIONS_PATH } from "./solutions";

export type LocalizedText = { en: string; es: string };

export type PublicTheme = {
  ink: string;
  paper: string;
  accent: string;
  muted: string;
  ctaInk: string;
};

export type NavLink = {
  label: LocalizedText | string;
  href?: string;
  anchor?: string;
};

export type AudienceItem = {
  name: LocalizedText | string;
  description?: LocalizedText | string;
  image?: string;
};

export type WorkItem = {
  title?: LocalizedText | string;
  alt?: LocalizedText | string;
  discipline?: LocalizedText | string;
  year?: string;
  image?: string;
};

export type TestimonialItem = {
  quote?: LocalizedText | string;
  name?: string;
  role?: LocalizedText | string;
  rating?: string;
  avatar?: string;
};

export type StatItem = {
  value: string;
  label: LocalizedText | string;
};

export type FeatureItem = {
  title: LocalizedText | string;
  description: LocalizedText | string;
  icon?: string;
};

export type BlogItem = {
  /** URL segment: the post lives at /blog/<slug>. Posts without a slug link to `href`. */
  slug?: string;
  category?: LocalizedText | string;
  title?: LocalizedText | string;
  excerpt?: LocalizedText | string;
  href?: string;
  /** ISO date (YYYY-MM-DD). Takes precedence over the free-text `date`. */
  publishedAt?: string;
  date?: LocalizedText | string;
  author?: string;
  image?: string;
  /** Post body in Markdown (see lib/markdown.tsx). */
  body?: LocalizedText | string;
  hidden?: boolean;
};

export type LogoItem = {
  name: string;
  image: string;
};

export type SolutionLine = {
  title: LocalizedText | string;
  description?: LocalizedText | string;
  linkLabel?: LocalizedText | string;
  href?: string;
  icon?: string;
};

export type FooterLink = {
  label: LocalizedText | string;
  href: string;
  soon?: boolean;
};

export const PUBLIC_THEME_DEFAULTS: PublicTheme = {
  ink: "#0B0F0F",
  paper: "#FFFFFF",
  accent: "#28F1E7",
  muted: "#B8CFCF",
  ctaInk: "#0B0F0F",
};

const L = (en: string, es: string): LocalizedText => ({ en, es });

const STALE_COPY_MARKERS = [
  "Bold Brands",
  "Marcas valientes",
  "Tampa Entrepreneurs",
  "Design, web, and wraps",
  "Diseno, web y wraps",
  "Diseño, web y wraps",
  "transforma ideas en productos",
  "transforms ideas into digital products",
];

/** Self-hosted copies of the Framer site's assets (apps/landing/public/site). */
const A = (file: string) => `/site/${file}`;
export const BLOG_PATH = "/blog";
export const CONTACT_PATH = "/contact";
export { SOLUTIONS_PATH };

export const WEBSITE_DEFAULTS = {
  header: {
    showCTA: true,
    sticky: true,
    logo: "",
    ctaText: L("Let's talk", "Hablemos"),
    ctaUrl: "#contact",
    navigation: [
      { label: L("Services", "Servicios"), anchor: "services" },
      { label: L("Solutions", "Soluciones"), href: SOLUTIONS_PATH },
      { label: L("Our Work", "Proyectos"), anchor: "work" },
      { label: L("Testimonials", "Testimonios"), anchor: "testimonials" },
      { label: L("Blog", "Blog"), anchor: "blog" },
    ] as NavLink[],
  },
  hero: {
    badge: L("We're here to help.", "estamos para ayudarte"),
    badgeAvatars: [A("hero-avatar-1.webp"), A("hero-avatar-2.webp")],
    title: L("Everything your Tampa business needs to keep growing", "Todo lo que tu negocio en Tampa necesita para seguir creciendo"),
    description: L("From light-up signs to your whole online presence, we've got your Tampa Bay business covered.", "Desde letreros luminosos hasta toda tu presencia en internet: nos encargamos de lo que tu negocio en Tampa Bay necesita."),
    ctaText: L("Get a Quote", "Pide tu cotización"),
    ctaUrl: "#contact",
    image: A("hero-bg.webp"),
  },
  audiences: {
    items: [
      {
        name: L("For Food Truck Owners", "Para dueños de food trucks"),
        description: L("We help more people find your truck and order from it, with LED signs, digital menus, and delivery apps.", "Hacemos que más gente encuentre tu food truck y te pida, con letreros LED, menús digitales y apps de delivery."),
        image: A("audience-food-trucks.png"),
      },
      {
        name: L("For Small Businesses", "Para pequeños negocios"),
        description: L("Think of us as your in-house marketing team: we handle everything from your storefront to your Google reviews and payment systems.", "Somos tu equipo de marketing: nos encargamos de todo, desde la fachada hasta tus reseñas en Google y tus sistemas de cobro."),
        image: A("audience-small-business.png"),
      },
      {
        name: L("For Event Organizers", "Para organizadores de eventos"),
        description: L("LED photo backdrops for your events, plus custom event websites with countdowns, invitations, and photo galleries.", "Fondos LED para fotos en tus eventos, y sitios web a la medida con cuenta regresiva, invitaciones y galerías de fotos."),
        image: A("audience-events.png"),
      },
    ] as AudienceItem[],
  },
  stats: {
    label: L("Trusted by businesses all over Tampa", "Negocios de todo Tampa confían en nosotros"),
    avatars: [A("trust-avatar-1.png"), A("trust-avatar-2.png"), A("trust-avatar-3.png")],
    logos: [
      { name: "Farandulero Restaurant", image: A("logos/farandulero.svg") },
      { name: "ByeBye Lawyer", image: A("logos/byebye-lawyer.svg") },
      { name: "Alo Cubanísimo", image: A("logos/alo-cubanisimo.svg") },
      { name: "Rosales Pressure Cleaning", image: A("logos/rosales.svg") },
      { name: "Sophi Nails", image: A("logos/sophi-nails.svg") },
      { name: "Gold Lashes", image: A("logos/gold-lashes.svg") },
      { name: "Asudar", image: A("logos/asudar.svg") },
      { name: "Premier Imports", image: A("logos/premier-imports.svg") },
    ] as LogoItem[],
    items: [
      { value: "45%", label: L("Increase in Digital Visibility", "de aumento en visibilidad digital") },
      { value: "98%", label: L("Customer Satisfaction", "de clientes satisfechos") },
      { value: "30%", label: L("Boost in Delivery Sales", "de aumento en ventas por delivery") },
      { value: "+1000", label: L("Feet of LED Lighting Installed", "pies de luces LED instaladas") },
    ] as StatItem[],
  },
  work: {
    badge: L("Selected work", "proyectos destacados"),
    title: L("Ideas we've brought to life", "Ideas que hicimos realidad"),
    description: L("This isn't just design. It's real work that grabs attention, drives sales, and builds brands people remember.", "No es solo diseño: son soluciones reales que llaman la atención, impulsan las ventas y crean marcas que la gente recuerda."),
    items: [
      { image: A("work-01.webp") },
      { image: A("work-02.webp") },
      { image: A("work-03.webp") },
      { image: A("work-04.webp") },
      { image: A("work-05.webp") },
      { image: A("work-06.webp") },
      { image: A("work-07.webp") },
      { image: A("work-08.webp") },
      { image: A("work-09.webp") },
      { image: A("work-10.webp") },
      { image: A("work-11.webp") },
      { image: A("work-12.webp") },
      { image: A("work-13.webp") },
      { image: A("work-14.webp") },
    ] as WorkItem[],
  },
  services: {
    badge: L("What we do", "Lo que hacemos"),
    title: L("We handle it all, from design to install", "Nos encargamos de todo, del diseño a la instalación"),
    description: L("Complete solutions for how your business or event looks and works, in person and online.", "Soluciones completas para que tu negocio o evento luzca y funcione bien, en persona y en internet."),
    featuresTitle: L("Why work with us", "Por qué trabajar con nosotros"),
    items: [
      {
        name: L("Visual Production", "Producción visual"),
        image: A("service-visual.webp"),
        description: L("LED neon signs, storefront signage, large-format banners, eye-catching posters, and printed menus, designed and built by us.", "Letreros de neón LED, rótulos para fachadas, banners de gran formato, pósters llamativos y menús impresos, diseñados y fabricados por nosotros."),
      },
      {
        name: L("Digital Presence", "Presencia digital"),
        image: A("service-digital.webp"),
        description: L("Full branding (logos and brand guidelines), SEO-ready websites, interactive QR menus, and social media management.", "Branding completo (logos y manual de marca), sitios web listos para SEO, menús QR interactivos y manejo de redes sociales."),
      },
      {
        name: L("Tech Setup", "Sistemas y conexiones"),
        image: A("service-operations.webp"),
        description: L("Google Business Profile setup, getting you on delivery apps (Uber Eats, DoorDash), and syncing it all with your POS (Square, Clover).", "Configuramos tu perfil de Google, te damos de alta en apps de delivery (Uber Eats, DoorDash) y lo conectamos todo con tu POS (Square, Clover)."),
      },
    ] as Array<{ name: LocalizedText | string; description?: LocalizedText | string; image?: string }>,
    features: [
      {
        title: L("All in One", "Todo en uno"),
        icon: A("icons/feature-integral.svg"),
        description: L("One team handles both your physical and online presence, so your brand looks the same everywhere.", "Un solo equipo maneja tu presencia física y digital, para que tu marca se vea igual en todas partes."),
      },
      {
        title: L("LED Technology", "Tecnología LED"),
        icon: A("icons/feature-led.svg"),
        description: L("We build our own energy-efficient LED neon, made to fit your storefront, interior, or event.", "Fabricamos nuestro propio neón LED de bajo consumo, hecho a la medida de tu fachada, tu local o tu evento."),
      },
      {
        title: L("Everything Connected", "Todo conectado"),
        icon: A("icons/feature-sync.svg"),
        description: L("We connect your in-store sales tools with your online and delivery platforms, so orders and payments just flow.", "Conectamos tus herramientas de venta en el local con tus plataformas digitales y de delivery, para que pedidos y pagos fluyan sin problemas."),
      },
      {
        title: L("Local Know-How", "Conocemos Tampa"),
        icon: A("icons/feature-local.svg"),
        description: L("Strategies made for Tampa Bay businesses and customers, so more people nearby find you.", "Estrategias pensadas para negocios y clientes de Tampa Bay, para que más gente cerca de ti te encuentre."),
      },
    ] as FeatureItem[],
  },
  cta: {
    title: L("Ready to keep your business growing?", "¿Listo para que tu negocio siga creciendo?"),
    ctaText: L("Get a Quote", "Pide tu cotización"),
    ctaUrl: "#contact",
  },
  testimonials: {
    badge: L("Testimonials", "Testimonios"),
    title: L("What our clients say", "Lo que dicen nuestros clientes"),
    description: L("Real stories from clients who've seen their ideas come to life with us.", "Historias reales de clientes que ya hicieron realidad sus ideas con nosotros."),
    items: [
      {
        quote: L("The light-up sign Raúl and Adrián made for the front of our restaurant looks amazing. Since the install, we're way more visible at night and it brings in a lot more foot traffic.", "El cartel lumínico que nos hicieron Raúl y Adrián para el frente del restaurante quedó espectacular. Desde la instalación, la visibilidad de noche mejoró notablemente y atrae a muchos más clientes de la calle."),
        name: "Carlos Mendoza",
        role: L("Owner of Taquería El Sol", "Propietario de Taquería El Sol"),
        rating: "5",
      },
      {
        quote: L(
          "We needed a large-format banner for our storefront before the grand opening. ON45D Studio designed and installed it in record time. Excellent print quality and a professional finish!",
          "Necesitábamos un banner de gran formato para la fachada de nuestra tienda antes de la gran apertura. El equipo de ON45D Studio lo diseñó e instaló en tiempo récord. ¡Excelente calidad de impresión y acabado profesional!"
        ),
        name: "Mariana",
        role: L("Founder of Boutique Palma", "Fundadora de Boutique Palma"),
        rating: "5",
      },
      {
        quote: L(
          "Working with ON45D Studio on my business website was a seamless experience. They helped us organize the information, improve Google ranking, and the design looks super clean on phones.",
          "Trabajar con ON45D Studio para el sitio web de mi negocio fue una experiencia impecable. Nos ayudaron a organizar la información, mejorar el posicionamiento en Google y el diseño se ve súper limpio en celulares."
        ),
        name: "Javier",
        role: L("AutoDetailing Tampa", "AutoDetailing Tampa"),
        rating: "5",
      },
      {
        quote: L(
          "The website for my quinceañera came out super modern, with a great countdown. Huge thanks to Adrián for customizing every detail and integrating the music and gift list.",
          "El website para mis 15 años quedó súper moderno y con una cuenta regresiva genial. Muchísimas gracias a Adrián por personalizar cada detalle e integrar la música y la lista de regalos."
        ),
        name: "Valeria Martínez",
        role: L("Quinceañera", "15 Años"),
        rating: "5",
      },
      {
        quote: L("I've worked with ON45D on several LED décor and display installs for corporate events. Raúl and Adrián are super professional, punctual, and creative.", "He colaborado con ON45D en varios proyectos de decoración LED e instalación de estructuras visuales para eventos corporativos. Raúl y Adrián son extremadamente profesionales, puntuales y creativos."),
        name: "Elena Fuentes",
        role: L("Event organizer", "Organizadora de Eventos"),
        rating: "5",
      },
      {
        quote: L("I wanted an LED sign for the main wall where customers take photos. ON45D Studio's design blew me away; it's perfect for Instagram and the finish is really professional. Everyone asks who made it, and I always send them to these guys.", "Buscaba un cartel de luces LED para la pared principal del local donde los clientes se toman fotos. El diseño de ON45D Studio superó todas mis expectativas; perfecta para fotos de Instagram y el acabado muy profesional. Todo el mundo me pregunta quién lo hizo y siempre les recomiendo a los muchachos."),
        name: "Esteban Ríos",
        role: L("Owner of Ríos Barbershop", "Dueño de Ríos Barbershop"),
        rating: "5",
      },
      {
        quote: L("We were looking for an all-in-one solution for our Tampa food truck, and finding ON45D was the best thing that could happen. Raúl handled the visual design and Adrián built the interactive menu app. Now customers scan the QR, see photos of the burgers, and orders move faster. We increased service speed by 40%!", "Estábamos buscando una solución integral para nuestro food truck en Tampa y dar con ON45D fue lo mejor que nos pudo pasar. Raúl se encargó del diseño visual y Adrián programó la app del menú interactivo. Ahora los clientes escanean el QR, ven las fotos de las hamburguesas y los pedidos salen más rápido. ¡Atendemos un 40% más rápido!"),
        name: 'Roberto "Tito" Fernández',
        role: L("Owner of Tito's Burger Truck", "Dueño de Tito's Burger Truck"),
        rating: "5",
      },
      {
        quote: L("We hired ON45D Studio to redesign our brand and set up the interactive menu on the bakery's tablets. Great design plus a clean tech setup took our business to the next level. Highly recommend.", "Contratamos a ON45D Studio para rediseñar nuestra marca e implementar el menú interactivo en las tabletas de la pastelería. La combinación de buen diseño e integración técnica impecable hizo que nuestro negocio diera un salto enorme. Súper recomendados."),
        name: "Elena Fuentes",
        role: L("Event organizer", "Organizadora de Eventos"),
        rating: "5",
      },
    ] as TestimonialItem[],
  },
  blog: {
    badge: L("Our blog", "nuestro blog"),
    title: L("Fresh from the blog", "Lo último del blog"),
    ctaLabel: L("See all posts", "Ver todo el blog"),
    ctaUrl: BLOG_PATH,
    author: "ON45D Studio",
    pageTitle: L("Ideas that elevate your brand.", "Ideas que elevan tu marca."),
    pageDescription: L(
      "Perspectives, processes, and lessons learned about design, development, and how to build a presence that works for your business.",
      "Perspectivas, procesos y aprendizajes sobre diseño, desarrollo y cómo construir una presencia que trabaja para tu negocio."
    ),
    pageImage: A("blog-hero-bg.webp"),
    promoTitle: L("Let's grow your Tampa business.", "Hagamos crecer tu negocio en Tampa."),
    promoText: L("Advertising and digital solutions that help you sell more.", "Publicidad y soluciones digitales para vender más."),
    promoCta: L("Get a Quote", "Pide tu cotización"),
    relatedTitle: L("Related articles", "Artículos relacionados"),
    items: BLOG_POST_DEFAULTS,
  },
  solutions: {
    showOnHome: true,
    badge: L("Software solutions", "soluciones de software"),
    title: L("Software we build and run ourselves", "Software que creamos y usamos"),
    description: L(
      "Beyond your physical and digital presence, we build apps, tools and AI integrations. These are already in use.",
      "Además de tu presencia física y digital, creamos apps, herramientas e integraciones de IA. Estas son las que ya están en uso."
    ),
    ctaText: L("See all solutions", "Ver todas las soluciones"),
    featuredCta: L("Learn more", "Ver más"),
    appStoreLabel: L("Download on the App Store", "Descárgala en el App Store"),
    lines: [
      {
        icon: "sparkles",
        title: L("AI integrations", "Integraciones de IA"),
        description: L(
          "Assistants and automation for email and customer service, connected to the tools you already use.",
          "Asistentes, automatización de correo y atención, conectados a las herramientas que ya usas."
        ),
        linkLabel: L("See solutions", "Ver soluciones"),
        href: SOLUTIONS_PATH,
      },
      {
        icon: "code",
        title: L("Custom apps and software", "Apps y software a medida"),
        description: L(
          "iPhone and web apps, internal dashboards and systems for your operation.",
          "Apps para iPhone y web, paneles internos y sistemas para tu operación."
        ),
        linkLabel: L("Tell us your idea", "Cuéntanos tu idea"),
        href: "#contact",
      },
    ] as SolutionLine[],
    pageBadge: L("ON45D solutions", "soluciones ON45D"),
    pageTitle: L("Software that solves real problems", "Software que resuelve problemas reales"),
    pageDescription: L(
      "Apps, tools and AI integrations built by ON45D Studio. Some are our own products; others are solutions built for our clients.",
      "Apps, herramientas e integraciones de IA creadas por ON45D Studio. Algunas son productos propios; otras, soluciones hechas para nuestros clientes."
    ),
    showSoonCard: true,
    soonKind: L("Artificial intelligence", "Inteligencia artificial"),
    soonTitle: L("Coming soon", "Próximamente"),
    soonText: L("Our next solution will appear here.", "Aquí aparecerá nuestra próxima solución."),
    ideaKind: L("Custom software", "Software a medida"),
    ideaTitle: L("Have an idea? Let's build it.", "¿Tienes una idea? Construyámosla."),
    ideaText: L("Apps, automation, and AI for your Tampa Bay business. No-pressure consultation.", "Apps, automatización e IA para tu negocio en Tampa Bay. Te asesoramos sin compromiso."),
    ideaCta: L("Get a Quote", "Pide tu cotización"),
    detailsLabel: L("View details", "Ver detalles"),
    availableLabel: L("Available", "Disponible"),
    soonLabel: L("Coming soon", "Próximamente"),
    privacyLabel: L("Privacy policy", "Política de privacidad"),
    termsLabel: L("Terms of service", "Términos de servicio"),
    supportLabel: L("Support", "Soporte"),
    dataBadge: L("Privacy", "privacidad"),
    byLabel: L("by ON45D Studio", "por ON45D Studio"),
    items: SOLUTION_DEFAULTS,
  },
  contact: {
    badge: L("Here when you need us", "Aquí cuando nos necesites"),
    title: L("Ready to give your business a fresh look?", "¿Listo para darle una nueva imagen a tu negocio?"),
    description: L("We'll take the time to understand exactly what your space or business needs.", "Nos tomamos el tiempo de entender lo que tu local o negocio necesita."),
    ctaText: L("Get a Quote", "Pide tu cotización"),
    email: "mailto:hello@on45d.studio",
    supportEmail: "mailto:support@on45d.studio",
    whatsapp: "https://wa.me/18137120063",
    enabled: true,
    pageTitle: L("Let's talk", "Hablemos"),
    pageDescription: L("Got a question about the agency, an idea for a collab, or need support? Drop us a message below and someone from our team will get back to you soon.", "¿Tienes una pregunta sobre la agencia, una idea para colaborar o necesitas soporte? Escríbenos abajo y alguien de nuestro equipo te responderá pronto."),
    pageImage: A("contact-hero-bg.webp"),
    generalTitle: L("General Questions", "Preguntas generales"),
    generalText: L("Questions about a service or product, collaborations, or anything else.", "Preguntas sobre algún servicio o producto, colaboraciones o cualquier otra cosa."),
    supportTitle: L("Customer Support", "Soporte a clientes"),
    supportText: L("Need help with an order or subscription, or running into a tech issue?", "¿Necesitas ayuda con un pedido o tu suscripción, o tienes algún problema técnico?"),
    socialTitle: L("Follow us", "Síguenos en redes"),
    serviceOptions: [
      L("Promotional Graphics", "Gráficos promocionales"),
      L("Storefront", "Fachada"),
      L("Light-Up Sign", "Letrero luminoso"),
      L("LED Accent Wall", "Pared decorativa LED"),
      L("Business Website", "Sitio web para negocio"),
      L("Digital Menu", "Menú digital"),
      L("Delivery Apps", "Apps de delivery"),
      L("POS Integration", "Integración POS"),
      L("Other", "Otro"),
    ] as LocalizedText[],
    submitText: L("Send Message", "Enviar mensaje"),
  },
  footer: {
    text: "© 2026 ON45D Studio LLC. All rights reserved.",
    tagline: L("Design and tech for businesses and events in Tampa Bay.", "Diseño y tecnología para negocios y eventos en Tampa Bay."),
    facebook: "https://www.facebook.com/profile.php?id=61585377830154",
    instagram: "https://www.instagram.com/on45dstudio/",
    linkedin: "https://www.linkedin.com/company/on45dstudio/",
    columns: [
      {
        title: L("Services", "Servicios"),
        links: [
          { label: L("Visual Production", "Producción visual"), href: "#services" },
          { label: L("Digital Presence", "Presencia digital"), href: "#services" },
          { label: L("Tech Setup", "Sistemas y conexiones"), href: "#services" },
          { label: L("Software Solutions", "Soluciones de software"), href: SOLUTIONS_PATH },
          { label: "All Services", href: "#services" },
        ] as FooterLink[],
      },
      {
        title: L("Agency", "Agencia"),
        links: [
          { label: "About us", href: "#hero", soon: true },
          { label: "Gallery", href: "#work", soon: true },
          { label: "Blog", href: BLOG_PATH },
          { label: L("Contact", "Contacto"), href: CONTACT_PATH },
        ] as FooterLink[],
      },
    ],
    newsletterTitle: "Get notified",
    newsletterText: L("Get news, promos, and our latest posts.", "Recibe novedades, promociones y nuestros últimos artículos."),
    newsletterLabel: L("Enter your email", "Escribe tu correo"),
    newsletterButton: L("Subscribe", "Suscribirme"),
  },
  seo: {
    metaTitle: "ON45D Studio",
    metaDescription: L(
      "One-stop advertising and digital integration solutions for businesses and events in Tampa Bay.",
      "Soluciones publicitarias e integraciones digitales de ventanilla única para comercios y eventos en Tampa Bay."
    ),
  },
  theme: { ...PUBLIC_THEME_DEFAULTS },
} as const;

export type WebsiteSectionKey = keyof typeof WEBSITE_DEFAULTS;
export const WEBSITE_SECTION_KEYS = Object.keys(WEBSITE_DEFAULTS) as WebsiteSectionKey[];

export function localizedText(value: unknown, locale: SupportedLocale, fallback = ""): string {
  const localized = getLocalizedValue<string>(value, locale);
  if (typeof localized === "string" && localized.trim()) return localized;
  if (typeof value === "string" && value.trim()) return value;
  return fallback;
}

export function isStaleWebsiteCopy(content: Record<string, unknown> | undefined | null): boolean {
  if (!content) return false;
  const hero = ((content.hero as { content?: unknown } | undefined)?.content ?? content.hero) as
    | { title?: unknown }
    | undefined;
  const title = JSON.stringify(hero?.title || "");
  return STALE_COPY_MARKERS.some((marker) => title.includes(marker));
}

export function mergeSection<K extends WebsiteSectionKey>(
  section: K,
  content: Record<string, unknown> | undefined | null
): (typeof WEBSITE_DEFAULTS)[K] & Record<string, unknown> {
  const defaults = WEBSITE_DEFAULTS[section] as Record<string, unknown>;
  return { ...defaults, ...(content && typeof content === "object" ? content : {}) } as (typeof WEBSITE_DEFAULTS)[K] &
    Record<string, unknown>;
}

export function resolvePublicTheme(content: Record<string, unknown> | undefined | null): PublicTheme {
  const merged = mergeSection("theme", content);
  const hex = (value: unknown, fallback: string) =>
    typeof value === "string" && /^#[0-9A-Fa-f]{6}$/.test(value) ? value : fallback;
  return {
    ink: hex(merged.ink ?? merged.backgroundColor, PUBLIC_THEME_DEFAULTS.ink),
    paper: hex(merged.paper ?? merged.textColor, PUBLIC_THEME_DEFAULTS.paper),
    accent: hex(merged.accent ?? merged.primaryColor, PUBLIC_THEME_DEFAULTS.accent),
    muted: hex(merged.muted ?? merged.secondaryColor, PUBLIC_THEME_DEFAULTS.muted),
    ctaInk: hex(merged.ctaInk, PUBLIC_THEME_DEFAULTS.ctaInk),
  };
}

export function navHref(item: NavLink | string): string {
  if (typeof item === "string") {
    const slug = item.toLowerCase().replace(/\s+/g, "-");
    return `#${slug}`;
  }
  if (item.href) return item.href;
  if (item.anchor) return item.anchor.startsWith("#") ? item.anchor : `#${item.anchor}`;
  return "#hero";
}

export function resolveNavItems(
  navigation: unknown,
  locale: SupportedLocale
): Array<{ label: string; href: string }> {
  const source = Array.isArray(navigation) && navigation.length ? navigation : WEBSITE_DEFAULTS.header.navigation;
  return source.map((item) => {
    const raw = item as NavLink | string;
    const label =
      typeof raw === "string"
        ? localizedText(raw, locale, raw)
        : localizedText(raw.label, locale, "Link");
    return { label, href: legacySitePath(navHref(raw)) };
  });
}

/**
 * Navigation saved before /solutions existed still gets its "Solutions" link, right after
 * Services (or first). Turn it off with `solutions.showInNav: false`.
 */
export function withSolutionsNav(
  items: Array<{ label: string; href: string }>,
  locale: SupportedLocale,
  show = true
): Array<{ label: string; href: string }> {
  if (!show || items.some((item) => item.href === SOLUTIONS_PATH || item.href.startsWith(`${SOLUTIONS_PATH}/`))) return items;
  const entry = { label: locale === "es" ? "Soluciones" : "Solutions", href: SOLUTIONS_PATH };
  const services = items.findIndex((item) => item.href === "#services");
  const next = [...items];
  next.splice(services + 1, 0, entry);
  return next;
}

/** Posts that are not hidden, in the order the editor lists them. */
export function visibleBlogPosts(items: unknown): BlogItem[] {
  const list = Array.isArray(items) && items.length ? (items as BlogItem[]) : BLOG_POST_DEFAULTS;
  return list.filter((item) => item && !item.hidden);
}

/** "#contact" CTAs (and CMS links saved with it) open the contact page, as on Framer. */
export function resolveSiteHref(href: string): string {
  if (href === "#contact") return CONTACT_PATH;
  return legacySitePath(href);
}

export function blogPostHref(item: BlogItem): string {
  if (item.slug) return `${BLOG_PATH}/${item.slug}`;
  return item.href || BLOG_PATH;
}

export function slugify(text: string): string {
  return text
    .normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "-")
    .replace(/^-+|-+$/g, "")
    .slice(0, 80);
}

/** "ago 2026" for cards, "23 ago 2026" for the post header (Framer's formats). */
export function formatPostDate(item: BlogItem, locale: SupportedLocale, withDay = false): string {
  if (item.publishedAt && /^\d{4}-\d{2}-\d{2}/.test(item.publishedAt)) {
    const date = new Date(`${item.publishedAt.slice(0, 10)}T12:00:00`);
    if (!Number.isNaN(date.getTime())) {
      const tag = locale === "es" ? "es-ES" : "en-US";
      const options: Intl.DateTimeFormatOptions = withDay
        ? { day: "numeric", month: "short", year: "numeric" }
        : { month: "short", year: "numeric" };
      return new Intl.DateTimeFormat(tag, options).format(date).replace(/\./g, "").replace(/ de /g, " ");
    }
  }
  return localizedText(item.date, locale);
}

export function themeStyle(theme: PublicTheme): Record<string, string> {
  return {
    "--public-ink": theme.ink,
    "--public-paper": theme.paper,
    "--public-accent": theme.accent,
    "--public-muted": theme.muted,
    "--public-cta-ink": theme.ctaInk,
  };
}
